import React, { useContext, useEffect, useState } from 'react';
import { Box, TextField } from '@material-ui/core';
import { HelpOutline } from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import PropTypes from 'prop-types';

import AddVendorDialog from '~/components/AddVendorDialog';
import Button from '~/components/Button';
import QuickBookRowAutocomplete from '~/components/QuickBookRow/QuickBookRowAutocomplete';
import QuickBookRowNotesAndAttachment from '~/components/QuickBookRow/QuickBookRowNotesAndAttachment';
import QuickBookRowScopeSelect from '~/components/QuickBookRow/QuickBookRowScopeSelect';
import {
  setInitialJournalBasedOnRecommendation,
  setInitialJournalScopeBasedOnAccount,
  setUpJournalForSubmit,
} from '~/components/QuickBookRow/transactionTemplates/helpers/journalFunctions';
import { useStyles } from '~/components/QuickBookRow/transactionTemplates/styles';
import { getManualBasicJournal } from '~/components/SearchSelect/TransactionTypeOptions';
import { PersonContext } from '~/contexts/PersonContext';
import { create } from '~/feathersFunctionalWrapper';
import { checkIsSmallScreen } from '~/functions/ScreenSizeFunctions';
import { useSegmentTrack } from '~/functions/SegmentFunctions';
import {
  getAccountOptions,
  getFixedAssetOptions,
  getVendorOptions,
} from '~/helpers/utils/functionalAutocompleteLibrary';

import QuickBookRowLoading from '../QuickBookRowLoading';

export default function QuickBookRowRefundReceivedTemplate({ transaction, onTransactionAction, onGetHelp }) {
  const { organizationId, organizationName, vendorTracking } = useContext(PersonContext);
  const isMobile = checkIsSmallScreen();
  const tracking = useSegmentTrack();
  const classes = useStyles();
  const [isLoading, setIsLoading] = useState(true);
  const [refundAccountOptions, setRefundAccountOptions] = useState([]);
  const [fixedAssetOptions, setFixedAssetOptions] = useState([]);
  const [vendorOptions, setVendorOptions] = useState([]);
  const [isAddVendorDialogOpen, setIsAddVendorDialogOpen] = useState(false);
  const [journal, setJournal] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const getData = async () => {
      if (!isLoading) {
        return;
      }

      const refundAccounts = await getAccountOptions(organizationId, {
        $or: [{ type: 'Expense' }, { type2: ['Sales and Use Tax', 'Fixed Asset'] }],
      });
      setRefundAccountOptions(refundAccounts);

      const fixedAssetOptions = await getFixedAssetOptions(organizationId);
      setFixedAssetOptions(fixedAssetOptions);

      if (vendorTracking) {
        const vendorOptions = await getVendorOptions(organizationId, true);
        setVendorOptions(vendorOptions);
      }

      const journalObject = getManualBasicJournal();
      const { account, id, description, date, amount } = transaction;
      journalObject.debitLines[0].accountIdSelect = account;
      journalObject.debitLines[0].yodleeTransactionId = id;
      journalObject.description = description;
      journalObject.date = date;
      journalObject.amount = amount;
      if (transaction.recommendedAccountId) {
        setInitialJournalBasedOnRecommendation({
          transaction,
          journalObject,
          accountOptions: refundAccounts,
          targetJournalLine: journalObject.creditLines[0],
          organizationName,
        });
      } else {
        await setInitialJournalScopeBasedOnAccount(transaction.account, journalObject);
      }

      setJournal(journalObject);
      setIsLoading(false);
    };
    getData();
  }, [isLoading, organizationId, organizationName, transaction, vendorTracking]);

  async function addVendor(newVendor) {
    const newVendorOptions = await getVendorOptions(organizationId, true);
    setVendorOptions(newVendorOptions);

    setJournal((prevJournal) => ({ ...prevJournal, vendorIdSelect: newVendor }));
    setIsAddVendorDialogOpen(false);
  }

  const addRefundReceived = async (event) => {
    event.preventDefault();

    if (isSubmitting) {
      return;
    }

    const journalData = {
      journal,
      organizationId,
      direction: 'cash in',
      transactionId: transaction.id,
      type: 'Refund',
    };

    const journalSubmit = setUpJournalForSubmit(journalData);

    journalSubmit.fixedAssetId = journal.fixedAssetIdSelect ? journal.fixedAssetIdSelect.id : null;

    setIsSubmitting(true);

    try {
      await create('journals', journalSubmit);
      setIsSubmitting(false);
      onTransactionAction();
    } catch (err) {
      setError(err);
      setIsSubmitting(false);
      return;
    }
  };

  if (isLoading) {
    return <QuickBookRowLoading />;
  }

  return (
    <>
      <AddVendorDialog
        isOpen={isAddVendorDialogOpen}
        closeDialog={() => {
          setIsAddVendorDialogOpen(false);
        }}
        onAddVendor={addVendor}
      />
      <form onSubmit={addRefundReceived}>
        <Box className={classes.rootContainer}>
          <Box className={classes.container}>
            <QuickBookRowAutocomplete
              className={classes.autoCompleteField}
              displaySubaccounts
              options={refundAccountOptions}
              onChange={(value) => {
                setJournal({ ...journal, creditLines: [{ ...journal.creditLines[0], accountIdSelect: value }] });
              }}
              label="Refund Account"
              value={journal.creditLines[0].accountIdSelect}
              required
            />
            {journal.creditLines[0].accountIdSelect?.type2 === 'Fixed Asset' && (
              <QuickBookRowAutocomplete
                className={classes.autoCompleteField}
                options={fixedAssetOptions}
                onChange={(value) => {
                  setJournal({ ...journal, fixedAssetIdSelect: value });
                }}
                label="Fixed Asset"
                value={journal.fixedAssetIdSelect}
                required
              />
            )}
            {vendorTracking && (
              <QuickBookRowAutocomplete
                className={classes.autoCompleteField}
                label="Vendor (optional)"
                options={vendorOptions}
                value={journal.vendorIdSelect}
                onChange={(value) => {
                  if (value?.id === 'New Instance') {
                    setIsAddVendorDialogOpen(true);
                    return;
                  }
                  setJournal({ ...journal, vendorIdSelect: value });
                }}
              />
            )}
          </Box>
          <TextField
            className={isMobile ? classes.mobileDescriptionField : classes.descriptionFieldSixtySixPercent}
            label="Description"
            placeholder="Description"
            variant="outlined"
            size="small"
            value={journal.description}
            onChange={(event) => {
              setJournal({ ...journal, description: event.target.value });
            }}
          />
          <QuickBookRowScopeSelect journal={journal} setJournal={setJournal} />
          <QuickBookRowNotesAndAttachment
            journal={journal}
            onChange={(newJournal) => {
              setJournal(newJournal);
            }}
          />
          {error && error.message ? (
            <Alert severity="error" className={classes.errorAlert}>
              {error.message}
            </Alert>
          ) : null}
          <Box className={classes.actionsContainer}>
            <Button
              variant="outlined"
              size="small"
              className={classes.helpButton}
              onClick={() => {
                tracking('get_help clicked', { location: 'Inline Booking - Import Feed' });
                onGetHelp();
              }}
            >
              <HelpOutline className={classes.helpIcon} />
              Get Help
            </Button>
            <Box className={classes.rowActionsSecondaryContainer}>
              <Button
                variant="contained"
                className={classes.saveButton}
                size="small"
                fullWidth={false}
                type="submit"
                onClick={() => {
                  tracking('save clicked', { location: 'import_feed rei' });
                }}
              >
                Save
              </Button>
            </Box>
          </Box>
        </Box>
      </form>
    </>
  );
}

QuickBookRowRefundReceivedTemplate.propTypes = {
  transaction: PropTypes.object.isRequired,
  onTransactionAction: PropTypes.func.isRequired,
  onGetHelp: PropTypes.func.isRequired,
};
